<template>
  <app-collapse class="mt-1">
    <app-collapse-item
      :is-visible="false"
      class="collapsible-section-bg"
      title="Transport documents"
    >
      <template #header>
        <span
          class="lead collapse-title"
        >
          <section-status-icon
            :has-data="hasTransportDocumentsData"
            :has-errors="hasSectionErrors"
          />
          Transport documents
        </span>
      </template>
      <!-- Tracking code as N703 row (bound to tracking_number) -->
      <div
        v-if="showTrackingN703Row"
        class="border rounded pl-3 pr-3 pt-1 mb-2"
      >
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group>
              <label>
                Transport document type <small class="text-muted">1205002000</small>
                <feather-icon
                  v-b-tooltip.hover.top="'Tracking code (N703) — stored in the Tracking code field'"
                  icon="InfoIcon"
                  size="14"
                  class="ml-50 text-muted"
                />
              </label>
              <v-select
                :value="TRANSPORT_N703_TYPE"
                :options="[{ label: TRANSPORT_N703_TYPE, value: TRANSPORT_N703_TYPE }]"
                :reduce="option => option.value"
                :clearable="false"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Tracking code"
              rules=""
            >
              <b-form-group>
                <label>
                  Transport document reference number <small class="text-muted">1205001000</small>
                </label>
                <b-form-input
                  v-model="parcel.tracking_number"
                  v-b-tooltip.hover.top="getIssueMessage('tracking_number')"
                  :class="getIssueClass('tracking_number')"
                  :disabled="disabled"
                  :state="errors.length > 0 || getIssueClass('tracking_number') ? false : null"
                />
                <small class="text-danger">{{ errors[0] || getIssueMessage('tracking_number') }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          />

          <b-col
            cols="12"
            md="1"
          />
        </b-row>
      </div>

      <div
        v-for="(doc, docIndex) in nonN703Documents"
        :key="doc._uiKey || docIndex"
        class="border rounded pl-3 pr-3 pt-1 mb-2"
      >
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Transport document type"
              :rules="rowHasData(doc) ? 'not_n703' : ''"
            >
              <b-form-group>
                <label>
                  Transport document type <small class="text-muted">1205002000</small>
                </label>
                <classifier-select
                  v-model="doc.transport_document_type"
                  :classifier-key="classifierKeys.transportDocumentTypes"
                  :country="classifierCountry"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                  placeholder="e.g. N355"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="4"
          >
            <validation-provider
              v-slot="{ errors }"
              name="Transport document reference number"
              rules=""
            >
              <b-form-group>
                <label>
                  Transport document reference number <small class="text-muted">1205001000</small>
                  <feather-icon
                    v-b-tooltip.hover.top="'Reference number of the transport document'"
                    icon="InfoIcon"
                    size="14"
                    class="ml-50 text-muted"
                  />
                </label>
                <b-form-input
                  v-model="doc.transport_document_reference_number"
                  v-b-tooltip.hover.top="getIssueMessage(documentIssuePath(doc, 'transport_document_reference_number'))"
                  :class="getIssueClass(documentIssuePath(doc, 'transport_document_reference_number'))"
                  :disabled="disabled"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="3"
          />

          <b-col
            cols="12"
            md="1"
            class="text-right"
          >
            <b-button
              v-if="!disabled"
              variant="link"
              size="sm"
              class="text-danger mt-2"
              @click="removeDocument(doc)"
            >
              <feather-icon icon="TrashIcon" />
              Delete
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-row>
        <b-col class="text-right">
          <b-button
            v-if="!disabled"
            variant="info"
            size="sm"
            @click="addDocument"
          >
            <feather-icon icon="PlusIcon" />
            New document
          </b-button>
        </b-col>
      </b-row>
    </app-collapse-item>
  </app-collapse>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BButton,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import errorHighlightingMixin from '@/mixins/errorHighlightingMixin'
import SectionStatusIcon from '@/views/shipments/declarations/modals/components/h1/SectionStatusIcon.vue'
import { notN703 } from '@validations'
import ClassifierSelect from '@/views/components/ClassifierSelect.vue'
import { CUSTOMS_CLASSIFIER_KEYS, classifierCountryParam } from '@/libs/customsClassifiers'

const TRANSPORT_N703_TYPE = 'N703'

export default {
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    ValidationProvider,
    vSelect,
    AppCollapse,
    AppCollapseItem,
    SectionStatusIcon,
    ClassifierSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [errorHighlightingMixin],
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    entryCountry: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      TRANSPORT_N703_TYPE,
      notN703,
      classifierKeys: CUSTOMS_CLASSIFIER_KEYS,
    }
  },
  computed: {
    // Tulli (CL754) snapshot is served only for FI; other countries use the generic list.
    classifierCountry() {
      return classifierCountryParam(this.entryCountry)
    },
    documentsList() {
      if (!this.parcel || !Array.isArray(this.parcel.transport_documents)) {
        return []
      }
      return this.parcel.transport_documents
    },
    nonN703Documents() {
      return this.documentsList.filter(doc => !this.isN703Type(doc && doc.transport_document_type))
    },
    hasTrackingNumberValue() {
      return !!(this.parcel && this.parcel.tracking_number)
    },
    showTrackingN703Row() {
      return true
    },
    hasTransportDocumentsData() {
      if (!this.parcel || !this.parcel.transport_documents || !Array.isArray(this.parcel.transport_documents)) {
        return false
      }
      return this.nonN703Documents.some(doc => doc.transport_document_type || doc.transport_document_reference_number) || this.hasTrackingNumberValue
    },
    hasSectionErrors() {
      return Object.keys(this.issuesMap).some(key => key.startsWith('transport_documents.') || key === 'tracking_number')
    },
  },
  watch: {
    parcel: {
      immediate: true,
      handler() {
        this.normalizeN703FromDocuments()
      },
    },
    'parcel.transport_documents': {
      deep: true,
      handler() {
        this.normalizeN703FromDocuments()
      },
    },
  },
  methods: {
    isN703Type(type) {
      if (type == null || type === '') {
        return false
      }
      const value = typeof type === 'object' && type !== null && 'value' in type ? type.value : type
      return String(value).toUpperCase() === TRANSPORT_N703_TYPE
    },
    /**
     * Move any N703 entries out of transport_documents into tracking_number.
     * tracking_number remains the single source of truth.
     */
    normalizeN703FromDocuments() {
      if (!this.parcel || !Array.isArray(this.parcel.transport_documents)) {
        return
      }
      const list = this.parcel.transport_documents
      const n703Entries = list.filter(doc => this.isN703Type(doc && doc.transport_document_type))
      if (n703Entries.length === 0) {
        return
      }
      const firstN703 = n703Entries[0]
      const hasTrackingNumber = !!(this.parcel.tracking_number)
      if (!hasTrackingNumber && firstN703.transport_document_reference_number) {
        this.$set(this.parcel, 'tracking_number', firstN703.transport_document_reference_number)
      }
      this.parcel.transport_documents = list.filter(doc => !this.isN703Type(doc && doc.transport_document_type))
    },
    documentIssuePath(doc, field) {
      const index = this.documentsList.indexOf(doc)
      if (index < 0) {
        return `transport_documents.0.${field}`
      }
      return `transport_documents.${index}.${field}`
    },
    addDocument() {
      if (!this.parcel.transport_documents) {
        this.$set(this.parcel, 'transport_documents', [])
      }
      this.parcel.transport_documents.push({
        transport_document_type: '',
        transport_document_reference_number: '',
      })
    },
    removeDocument(doc) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to delete this transport document?', {
        title: 'Please Confirm',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'Yes',
        cancelTitle: 'No',
        centered: true,
      }).then(value => {
        if (value) {
          const index = this.documentsList.indexOf(doc)
          if (index >= 0) {
            this.parcel.transport_documents.splice(index, 1)
          }
        }
      })
    },
    rowHasData(doc) {
      return !!(doc.transport_document_type || doc.transport_document_reference_number)
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
@import '@core/scss/base/core/mixins/declarations-mixins';

.collapsible-section-bg {
  @include collapsible-section-bg;
}

.input-warning {
  @include input-warning;
}

.input-danger {
  @include input-danger;
}
</style>
